import React, { useState, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import TranslateIcon from "@material-ui/icons/Translate";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";

import { i18n } from "../../translate/i18n";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import FRFlag from "../../assets/france.png";
import useSettings from "../../hooks/useSettings";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Español", icon: ESFlag },
    { value: "fr", label: "Français", icon: FRFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => {
    return {
        languagePopover: {
            padding: theme.spacing(2),
            width: 360,
            borderRadius: 16,
            background: theme.palette.type === "light" ? "rgba(255, 255, 255, 0.95)" : "rgba(40, 40, 40, 0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            border: theme.palette.type === "light" ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)",
        },
        languageHeader: {
            display: "flex",
            alignItems: "center",
            marginBottom: theme.spacing(2),
            position: "relative",
        },
        languageIconBox: {
            width: 48,
            height: 48,
            borderRadius: 12,
            background: theme.palette.type === "light" ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#3b82f6",
            marginRight: theme.spacing(2),
        },
        languageTitle: {
            fontSize: "1.1rem",
            fontWeight: 700,
            color: theme.palette.type === "light" ? "#1f2937" : "#f3f4f6",
            lineHeight: 1.2,
        },
        languageSubtitle: {
            fontSize: "0.85rem",
            color: theme.palette.type === "light" ? "#6b7280" : "#9ca3af",
            marginTop: 4,
        },
        languageList: {
            display: "flex",
            flexDirection: "column",
            gap: 8,
        },
        languageItem: {
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            borderRadius: 12,
            cursor: "pointer",
            transition: "all 0.2s ease",
            background: theme.palette.type === "light" ? "#f9fafb" : "rgba(255,255,255,0.05)",
            border: "1px solid transparent",
            "&:hover": {
                background: theme.palette.type === "light" ? "#eff6ff" : "rgba(59, 130, 246, 0.15)",
                borderColor: "rgba(59, 130, 246, 0.3)",
                transform: "translateY(-1px)",
            },
        },
        languageItemActive: {
            background: theme.palette.type === "light" ? "#3b82f6 !important" : "#2563eb !important",
            color: "#ffffff",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            "& $languageItemLabel": {
                color: "#ffffff",
            },
        },
        languageItemFlag: {
            width: 28,
            height: 28,
            borderRadius: "50%",
            objectFit: "cover",
            marginRight: 16,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        },
        languageItemLabel: {
            fontSize: "0.95rem",
            fontWeight: 600,
            color: theme.palette.type === "light" ? "#4b5563" : "#d1d5db",
            flex: 1,
        },
        languageItemCheck: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
        },
        closeButton: {
            position: "absolute",
            right: -8,
            top: -8,
            color: theme.palette.type === "light" ? "#9ca3af" : "#6b7280",
            "&:hover": {
                color: theme.palette.type === "light" ? "#4b5563" : "#d1d5db",
                background: theme.palette.type === "light" ? "#f3f4f6" : "rgba(255,255,255,0.1)",
            },
        },
        languageSelector: {
            position: "absolute",
            top: "15px",
            left: "15px",
            zIndex: 1000,
        },
    };
});

const PublicLanguageSelector = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { getPublicSetting } = useSettings();

    const [langAnchorEl, setLangAnchorEl] = useState(null);
    const [, setForceUpdate] = useState(Date.now());
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);

    useEffect(() => {
        const handleLanguageChanged = () => {
            setForceUpdate(Date.now());
        };
        i18n.on("languageChanged", handleLanguageChanged);
        return () => {
            i18n.off("languageChanged", handleLanguageChanged);
        };
    }, []);

    useEffect(() => {
        const getCompanyIdFromUrl = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const companyId = urlParams.get("companyId");
            return companyId ? parseInt(companyId) : null;
        };

        const companyId = getCompanyIdFromUrl();

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try {
                    if (langs) arr = JSON.parse(langs);
                } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => {
                setEnabledLanguages(["pt-BR", "en"]);
            });
    }, [getPublicSetting]);

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
        localStorage.setItem("i18nextLng", language);
        setLangAnchorEl(null);
    };

    const currentLang = languageOptions.find(l => i18n.language.startsWith(l.value) || l.value.startsWith(i18n.language)) || languageOptions[0];

    return (
        <div className={classes.languageSelector}>
            <button
                onClick={(e) => setLangAnchorEl(e.currentTarget)}
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    paddingRight: "20px",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: theme.palette.type === "light" ? "2px solid #fff" : "2px solid rgba(255,255,255,0.2)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    background: theme.palette.type === "light" ? "white" : "rgba(0,0,0,0.4)",
                    backdropFilter: "blur(4px)",
                }}>
                    <img
                        src={currentLang.icon}
                        alt={currentLang.label}
                        style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                    />
                </div>
            </button>

            <Popover
                id="language-popover"
                open={Boolean(langAnchorEl)}
                anchorEl={langAnchorEl}
                onClose={() => setLangAnchorEl(null)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                classes={{ paper: classes.languagePopover }}
            >
                <div className={classes.languageHeader}>
                    <div className={classes.languageIconBox}>
                        <TranslateIcon style={{ fontSize: 24 }} />
                    </div>
                    <div style={{ marginRight: 16 }}>
                        <Typography className={classes.languageTitle}>
                            {i18n.t("layout.languageModal.title")}
                        </Typography>
                        <Typography className={classes.languageSubtitle}>
                            {i18n.t("layout.languageModal.subtitle")}
                        </Typography>
                    </div>
                    <IconButton
                        className={classes.closeButton}
                        onClick={() => setLangAnchorEl(null)}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <div className={classes.languageList}>
                    {languageOptions
                        .filter((opt) => enabledLanguages.includes(opt.value))
                        .map((lang) => {
                            const isActive = i18n.language === lang.value;
                            return (
                                <div
                                    key={lang.value}
                                    className={clsx(classes.languageItem, {
                                        [classes.languageItemActive]: isActive,
                                    })}
                                    onClick={() => handleLanguageChange(lang.value)}
                                >
                                    <img
                                        src={lang.icon}
                                        alt={lang.label}
                                        className={classes.languageItemFlag}
                                    />
                                    <Typography className={classes.languageItemLabel}>
                                        {lang.value === "pt-BR" ? "Português" : lang.value === "es" ? "Español" : lang.value === "en" ? "English" : lang.label}
                                    </Typography>
                                    {isActive && (
                                        <div className={classes.languageItemCheck}>
                                            <CheckIcon style={{ fontSize: 16, color: "#fff" }} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
            </Popover>
        </div>
    );
};

export default PublicLanguageSelector;
